import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import App from "./App";
import "./lib/nav-history"; // capture entry history length at bootstrap (before any navigation)
import "./index.css";

// Initialize Sentry before rendering
const sentryDsn =
  typeof import.meta.env.VITE_SENTRY_DSN === "string"
    ? import.meta.env.VITE_SENTRY_DSN.trim()
    : "";

function isValidSentryDsn(dsn: string) {
  try {
    const url = new URL(dsn);
    return url.protocol === "https:" && url.username.length > 0 && url.hostname.endsWith(".sentry.io") && url.pathname.length > 1;
  } catch {
    return false;
  }
}

if (sentryDsn && !import.meta.env.DEV && isValidSentryDsn(sentryDsn)) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// A failed dynamic import almost always means a deploy replaced the hashed
// asset files under an open tab. Reload once to pick up the new build instead
// of surfacing a "Failed to fetch dynamically imported module" error screen.
window.addEventListener("vite:preloadError", (event) => {
  const lastReload = Number(sessionStorage.getItem("chunk-reload-at") || 0);
  if (Date.now() - lastReload < 60_000) return; // already retried — let the error surface
  sessionStorage.setItem("chunk-reload-at", String(Date.now()));
  event.preventDefault();
  window.location.reload();
});

// Get Clerk publishable key from environment
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  console.warn("Missing VITE_CLERK_PUBLISHABLE_KEY - auth will not work");
}

createRoot(document.getElementById("root")!).render(
  <ClerkProvider publishableKey={PUBLISHABLE_KEY || ""}>
    <App />
  </ClerkProvider>
);

// Unregister any stale service workers and clear caches to fix stuck PWAs
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
    }
  });
  caches.keys().then((keys) => {
    for (const key of keys) {
      caches.delete(key);
    }
  });
}
